import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/i18n/LangContext";
import { API_BASE } from "@/lib/api";
import Spinner from "@/components/Spinner";
import AdBanner from "@/components/AdBanner";

interface UpcomingGame {
  appid: string;
  name: string;
  capsule_image: string;
  release_date: string;
  price_text: string;
  platforms: { windows: boolean; mac: boolean; linux: boolean };
  review_summary: string;
  source: "upcoming" | "recent";
}

type CalendarFilter = "all" | "upcoming" | "recent";

function parseReleaseDate(dateStr: string): Date | null {
  const krMatch = dateStr.match(/(\d{4})년\s*(\d{1,2})월\s*(\d{1,2})일/);
  if (krMatch) return new Date(parseInt(krMatch[1]), parseInt(krMatch[2]) - 1, parseInt(krMatch[3]));
  // "Mar 29, 2026" or "March 29, 2026"
  const enMatch = dateStr.match(/(\w+)\s+(\d{1,2}),?\s+(\d{4})/);
  if (enMatch) { const d = new Date(`${enMatch[1]} ${enMatch[2]}, ${enMatch[3]}`); if (!isNaN(d.getTime())) return d; }
  // "29 Mar, 2026" or "29 March 2026"
  const enMatch2 = dateStr.match(/(\d{1,2})\s+(\w+),?\s+(\d{4})/);
  if (enMatch2) { const d = new Date(`${enMatch2[2]} ${enMatch2[1]}, ${enMatch2[3]}`); if (!isNaN(d.getTime())) return d; }
  return null;
}

const DAY_NAMES = ["일", "월", "화", "수", "목", "금", "토"];

export default function CalendarPage() {
  const { t } = useLang();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth());
  const [games, setGames] = useState<UpcomingGame[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState<number | null>(null);
  const [viewMode, setViewMode] = useState<"calendar" | "list">("calendar");
  const [filter, setFilter] = useState<CalendarFilter>("all");

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [upRes, recRes] = await Promise.allSettled([
          fetch(`${API_BASE}/store/upcoming?type=upcoming&count=100&lang=ko`),
          fetch(`${API_BASE}/store/upcoming?type=recent&count=100&lang=ko`),
        ]);
        const all: UpcomingGame[] = [];
        if (upRes.status === "fulfilled") { const d = await upRes.value.json(); all.push(...(d.results || []).map((g: UpcomingGame) => ({ ...g, source: "upcoming" as const }))); }
        if (recRes.status === "fulfilled") { const d = await recRes.value.json(); all.push(...(d.results || []).map((g: UpcomingGame) => ({ ...g, source: "recent" as const }))); }
        setGames(all);
      } catch { setGames([]); }
      setLoading(false);
    })();
  }, []);

  const filteredGames = useMemo(() => filter === "all" ? games : games.filter((g) => g.source === filter), [games, filter]);

  const gamesByDay = useMemo(() => {
    const map: Record<number, UpcomingGame[]> = {};
    for (const game of filteredGames) {
      const d = parseReleaseDate(game.release_date);
      if (d && d.getFullYear() === year && d.getMonth() === month) {
        const day = d.getDate();
        if (!map[day]) map[day] = [];
        map[day].push(game);
      }
    }
    return map;
  }, [filteredGames, year, month]);

  const monthGames = useMemo(() => {
    const all: { day: number; game: UpcomingGame }[] = [];
    for (const [day, dayGames] of Object.entries(gamesByDay)) {
      for (const game of dayGames) all.push({ day: parseInt(day), game });
    }
    return all.sort((a, b) => a.day - b.day);
  }, [gamesByDay]);

  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDay = new Date(year, month, 1).getDay();
  const today = new Date();
  const isCurrentMonth = today.getFullYear() === year && today.getMonth() === month;

  const prevMonth = () => { if (month === 0) { setYear(year - 1); setMonth(11); } else setMonth(month - 1); setSelectedDay(null); };
  const nextMonth = () => { if (month === 11) { setYear(year + 1); setMonth(0); } else setMonth(month + 1); setSelectedDay(null); };
  const goToday = () => { setYear(now.getFullYear()); setMonth(now.getMonth()); setSelectedDay(null); };

  const selectedDayGames = selectedDay ? (gamesByDay[selectedDay] || []) : [];

  return (
    <div className="max-w-[1320px] mx-auto px-4 sm:px-6 flex-1 w-full">
      {/* Header */}
      <div className="bg-card-bg dark:bg-card-bg rounded-2xl border border-gray-200 dark:border-card-border shadow-sm p-4 sm:p-5 mt-6 mb-6">
        <div className="flex items-center gap-2.5 mb-2">
          <div className="w-8 h-8 rounded-lg bg-gray-900 dark:bg-card-border flex items-center justify-center">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>
          </div>
          <h1 className="text-sm sm:text-base font-bold text-gray-900 dark:text-white">{t.calendar?.title || "출시 캘린더"}</h1>
        </div>
        <p className="text-xs sm:text-sm text-gray-500 dark:text-muted leading-relaxed">{t.calendar?.description || "Steam 게임 출시 일정을 캘린더로 확인하세요."}</p>
      </div>

      {/* Controls */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <button onClick={prevMonth} className="w-9 h-9 flex items-center justify-center rounded-lg bg-card-bg border border-gray-200 dark:border-card-border hover:bg-hover-bg transition-colors text-gray-600 dark:text-muted">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M15 18l-6-6 6-6" /></svg>
          </button>
          <h2 className="text-lg font-bold text-gray-900 dark:text-white tabular-nums min-w-[120px] text-center">
            {new Date(year, month).toLocaleDateString("ko-KR", { year: "numeric", month: "long" })}
          </h2>
          <button onClick={nextMonth} className="w-9 h-9 flex items-center justify-center rounded-lg bg-card-bg border border-gray-200 dark:border-card-border hover:bg-hover-bg transition-colors text-gray-600 dark:text-muted">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 18l6-6-6-6" /></svg>
          </button>
          {!isCurrentMonth && (
            <button onClick={goToday} className="ml-1 px-3 py-1.5 rounded-lg text-xs font-semibold bg-gray-900 text-white hover:bg-gray-800 transition-colors">{t.calendar?.today || "오늘"}</button>
          )}
        </div>
        <div className="flex items-center gap-2">
          {/* Filter */}
          <div className="flex items-center gap-1 bg-card-bg border border-gray-200 dark:border-card-border rounded-lg p-0.5">
            {([{ key: "all" as CalendarFilter, label: t.common?.all || "전체" }, { key: "recent" as CalendarFilter, label: t.calendar?.recentRelease || "최근 출시" }, { key: "upcoming" as CalendarFilter, label: t.calendar?.upcomingRelease || "출시 예정" }]).map((tab) => (
              <button key={tab.key} onClick={() => setFilter(tab.key)} className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${filter === tab.key ? "bg-gray-900 text-white" : "text-gray-500 dark:text-muted hover:text-gray-900 dark:hover:text-white"}`}>{tab.label}</button>
            ))}
          </div>
          {/* View mode */}
          <div className="flex items-center gap-1 bg-card-bg border border-gray-200 dark:border-card-border rounded-lg p-0.5">
            <button onClick={() => setViewMode("calendar")} className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${viewMode === "calendar" ? "bg-gray-900 text-white" : "text-gray-500 dark:text-muted"}`}>{t.calendar?.calendarView || "캘린더"}</button>
            <button onClick={() => setViewMode("list")} className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${viewMode === "list" ? "bg-gray-900 text-white" : "text-gray-500 dark:text-muted"}`}>{t.calendar?.listView || "리스트"}</button>
          </div>
        </div>
      </div>

      {loading ? (
        <Spinner />
      ) : viewMode === "calendar" ? (
        <div className="flex flex-col lg:flex-row gap-4 pb-8">
          {/* Calendar grid */}
          <div className="flex-1">
            <div className="bg-card-bg rounded-2xl border border-gray-200 dark:border-card-border shadow-sm overflow-hidden">
              {/* Day names */}
              <div className="grid grid-cols-7 border-b border-gray-200 dark:border-card-border">
                {DAY_NAMES.map((day, i) => (
                  <div key={day} className={`py-2.5 text-center text-xs font-semibold ${i === 0 ? "text-red-500" : i === 6 ? "text-blue-500" : "text-gray-500 dark:text-muted"}`}>{day}</div>
                ))}
              </div>

              {/* Date grid */}
              <div className="grid grid-cols-7">
                {Array.from({ length: firstDay }).map((_, i) => (
                  <div key={`e-${i}`} className="min-h-[80px] sm:min-h-[100px] border-b border-r border-gray-100 dark:border-card-border/50 bg-inner-bg/50 dark:bg-inner-bg/30" />
                ))}
                {Array.from({ length: daysInMonth }).map((_, i) => {
                  const day = i + 1;
                  const dow = (firstDay + i) % 7;
                  const isToday = isCurrentMonth && day === today.getDate();
                  const dayGames = gamesByDay[day] || [];
                  const hasGames = dayGames.length > 0;
                  const isSelected = selectedDay === day;
                  const upCount = dayGames.filter((g) => g.source === "upcoming").length;
                  const recCount = dayGames.filter((g) => g.source === "recent").length;

                  return (
                    <div key={day} onClick={() => hasGames ? setSelectedDay(isSelected ? null : day) : undefined} className={`min-h-[80px] sm:min-h-[100px] border-b border-r border-gray-100 dark:border-card-border/50 p-1.5 sm:p-2 transition-colors ${hasGames ? "cursor-pointer hover:bg-hover-bg dark:hover:bg-hover-bg" : ""} ${isSelected ? "bg-hover-bg dark:bg-hover-bg ring-2 ring-inset ring-blue-400" : ""}`}>
                      <div className="flex items-center justify-between mb-1">
                        <span className={`text-xs sm:text-sm font-semibold tabular-nums ${isToday ? "w-6 h-6 sm:w-7 sm:h-7 flex items-center justify-center rounded-full bg-gray-900 text-white" : dow === 0 ? "text-red-500" : dow === 6 ? "text-blue-500" : "text-gray-700 dark:text-foreground"}`}>{day}</span>
                        {hasGames && <span className="text-[10px] font-bold text-gray-500 dark:text-muted tabular-nums">{dayGames.length}</span>}
                      </div>
                      {/* Desktop preview */}
                      <div className="hidden sm:flex flex-col gap-0.5">
                        {dayGames.slice(0, 3).map((g) => (
                          <p key={g.appid} className={`text-[10px] truncate leading-tight ${g.source === "recent" ? "text-blue-600 dark:text-blue-400" : "text-orange-600 dark:text-orange-400"}`}>{g.name}</p>
                        ))}
                        {dayGames.length > 3 && <p className="text-[10px] text-gray-400 dark:text-sub-text">+{dayGames.length - 3}</p>}
                      </div>
                      {/* Mobile dots */}
                      {hasGames && (
                        <div className="sm:hidden flex gap-0.5 mt-1">
                          {recCount > 0 && <div className="w-1.5 h-1.5 rounded-full bg-blue-500" />}
                          {upCount > 0 && <div className="w-1.5 h-1.5 rounded-full bg-orange-500" />}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Legend */}
            <div className="mt-4 flex items-center gap-4 px-1 flex-wrap">
              <p className="text-xs text-gray-500 dark:text-muted">이번 달 <span className="font-bold text-gray-900 dark:text-white">{monthGames.length}</span>개</p>
              <div className="flex items-center gap-1.5"><div className="w-2 h-2 rounded-full bg-blue-500" /><span className="text-[10px] text-gray-400 dark:text-sub-text">최근 출시</span></div>
              <div className="flex items-center gap-1.5"><div className="w-2 h-2 rounded-full bg-orange-500" /><span className="text-[10px] text-gray-400 dark:text-sub-text">출시 예정</span></div>
            </div>
          </div>

          {/* Selected day panel */}
          <div className="w-full lg:w-80 shrink-0">
            <div className="sticky top-20">
              {selectedDay && selectedDayGames.length > 0 ? (
                <div className="bg-card-bg rounded-2xl border border-gray-200 dark:border-card-border shadow-sm overflow-hidden">
                  <div className="p-4 border-b border-gray-200 dark:border-card-border">
                    <h3 className="text-sm font-bold text-gray-900 dark:text-white">{new Date(year, month, selectedDay).toLocaleDateString("ko-KR", { month: "long", day: "numeric" })}</h3>
                    <p className="text-xs text-gray-500 dark:text-muted mt-0.5">{selectedDayGames.length}개 게임</p>
                  </div>
                  <div className="flex flex-col divide-y divide-gray-100 dark:divide-card-border/50 max-h-[60vh] overflow-y-auto">
                    {selectedDayGames.map((game) => (
                      <Link key={game.appid} to={`/game/${game.appid}`} className="flex items-center gap-3 p-3 hover:bg-inner-bg dark:hover:bg-hover-bg transition-colors group">
                        <img src={game.capsule_image || `https://shared.cloudflare.steamstatic.com/store_item_assets/steam/apps/${game.appid}/header.jpg`} alt="" className="w-[60px] h-[28px] rounded object-cover bg-hover-bg shrink-0" loading="lazy" />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-semibold text-gray-900 dark:text-white truncate group-hover:text-blue-600 transition-colors">{game.name}</p>
                          <div className="flex items-center gap-1.5 mt-0.5">
                            <span className={`text-[9px] font-bold px-1 rounded ${game.source === "recent" ? "bg-blue-100 dark:bg-blue-500/20 text-blue-600 dark:text-blue-400" : "bg-orange-100 dark:bg-orange-500/20 text-orange-600 dark:text-orange-400"}`}>
                              {game.source === "recent" ? "출시됨" : "예정"}
                            </span>
                            {game.price_text && <span className="text-[9px] text-gray-500 dark:text-muted">{game.price_text}</span>}
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="bg-card-bg rounded-2xl border border-gray-200 dark:border-card-border shadow-sm p-8 text-center">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="mx-auto text-gray-300 dark:text-sub-text mb-3"><rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>
                  <p className="text-xs text-gray-400 dark:text-sub-text">날짜를 선택하면<br />게임 목록이 표시돼요</p>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        /* List view — 날짜별 그룹 */
        <div className="pb-8">
          {monthGames.length === 0 ? (
            <div className="text-center py-12 text-sm text-gray-400 dark:text-sub-text">이 달에 게임이 없어요</div>
          ) : (
            (() => {
              // 날짜별로 그룹핑
              const grouped: Record<number, typeof monthGames> = {};
              for (const item of monthGames) {
                if (!grouped[item.day]) grouped[item.day] = [];
                grouped[item.day].push(item);
              }
              const sortedDays = Object.keys(grouped).map(Number).sort((a, b) => a - b);

              return sortedDays.map((day) => {
                const dayGamesForList = grouped[day];
                const dow = new Date(year, month, day).getDay();
                const dayNames = ["일", "월", "화", "수", "목", "금", "토"];
                const isPast = new Date(year, month, day) < today;

                return (
                  <div key={day}>
                    <div className={`flex items-center gap-3 mt-3 mb-1.5 ${isPast ? "opacity-50" : ""}`}>
                      <h3 className="text-sm font-bold tabular-nums text-gray-400 dark:text-sub-text">
                        {month + 1}월 {day}일 ({dayNames[dow]})
                      </h3>
                      <span className="text-xs text-gray-400 dark:text-sub-text">{dayGamesForList.length}개</span>
                      <div className="flex-1 h-px bg-gray-200 dark:bg-card-border" />
                    </div>
                    <div className={`mb-1 ${isPast ? "opacity-60" : ""}`}>
                      <div className="bg-card-bg dark:bg-card-bg rounded-lg border border-gray-200 dark:border-card-border overflow-hidden">
                        {dayGamesForList.map(({ game }, idx) => (
                          <Link key={game.appid} to={`/game/${game.appid}`} className={`group flex items-center gap-2 px-2.5 py-1.5 hover:bg-hover-bg dark:hover:bg-hover-bg transition-colors ${idx > 0 ? "border-t border-gray-100 dark:border-card-border/60" : ""}`}>
                            <div className="relative shrink-0 w-[60px] h-[28px] rounded overflow-hidden bg-hover-bg dark:bg-background hidden sm:block">
                              <img src={game.capsule_image || `https://shared.cloudflare.steamstatic.com/store_item_assets/steam/apps/${game.appid}/header.jpg`} alt={game.name} className="w-full h-full object-cover" loading="lazy" />
                            </div>
                            <p className="flex-1 min-w-0 text-xs sm:text-sm text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 font-medium truncate transition-colors">{game.name}</p>
                            <span className={`shrink-0 text-[9px] font-bold px-1 py-0.5 rounded ${game.source === "recent" ? "bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400" : "bg-blue-50 dark:bg-blue-500/10 text-blue-600 dark:text-blue-400"}`}>
                              {game.source === "recent" ? "출시" : "예정"}
                            </span>
                            <span className="shrink-0 text-[11px] text-gray-600 dark:text-sub-text font-medium tabular-nums min-w-[55px] text-right">{game.price_text || ""}</span>
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              });
            })()
          )}
        </div>
      )}
      <AdBanner className="my-4" />
    </div>
  );
}
